import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import { useForm } from 'react-hook-form';
import type { ModalProps } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { AddCircleOutline, CancelOutlined } from '@mui/icons-material';

import { useAddGivingAccountFundingTransactionMutation, GivingAccount } from 'generated/graphql';
import { currencies, Currency } from 'utils/currencyFormat';
import { selectUser } from 'entities/user/selectors';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { controlledFileUpload } from 'components/ReactHookForm/controlledFileUpload';
import { controlledDatePicker } from 'components/ReactHookForm/controlledDatePicker';

export const START_DATE_OF_BATCH_FEATURE = '2020-01-01';

type FieldValues = {
  processedAt: Moment;
  currencyCode: string;
  amount: number;
  note: string;
  givingAccountId: string;
  identifier: string;
  receiptUrl: string;
};

const ProcessedAtInput = controlledDatePicker<FieldValues>()('processedAt', {
  rules: {
    required: true,
    validate: {
      minDate: (date) => moment.utc(START_DATE_OF_BATCH_FEATURE).isSameOrBefore(date),
      maxDate: (date) => moment.utc().isSameOrAfter(moment.utc(date).startOf('day')),
    },
  },
});

const CurrencyCodeInput = controlledTextField<FieldValues>()('currencyCode', {
  rules: { required: true },
});

const AmountInput = controlledTextField<FieldValues>()('amount', {
  rules: { required: true, min: 1 },
});

const IdentifierInput = controlledTextField<FieldValues>()('identifier', {
  rules: { required: true },
});

const NoteInput = controlledTextField<FieldValues>()('note', {
  rules: { required: true },
});

const ReceiptUrlInput = controlledFileUpload<FieldValues>()('receiptUrl', {
  rules: { required: true },
});

export const AddGivingAccountFundingModal = ({
  givingAccount,
  style,
  onClickClose = (): void => {},
  ...props
}: {
  givingAccount: GivingAccount;
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>): JSX.Element => {
  const user = useSelector(selectUser);
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, isValid, isSubmitting },
    getValues,
    control,
    reset,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      processedAt: moment(),
      currencyCode: givingAccount?.currencyCode,
      amount: 0,
      note: '',
      givingAccountId: givingAccount?.id,
      identifier: `${user?.organization.name}_FUNDING_${givingAccount.provider}_${new Date()
        .toISOString()
        .slice(0, 10)}`,
      receiptUrl: '',
    },
  });
  const [
    addGivingAccountFundingTransactionMutation,
    { error: addGivingAccountFundingTransactionMutationError },
  ] = useAddGivingAccountFundingTransactionMutation({
    refetchQueries: ['givingAccountSummary', 'givingAccountTransactions'],
  });

  useEffect(() => {
    reset();
  }, [props.open]);

  const formValues = getValues();
  const handleSubmit = reactHookFormHandleSubmit(({ currencyCode: _currencyCode, ...data }) =>
    addGivingAccountFundingTransactionMutation({
      variables: {
        data: {
          identifier: data.identifier,
          givingAccountId: data.givingAccountId,
          note: data.note,
          receiptUrl: data.receiptUrl,
          processedAt: data.processedAt.toISOString(),
          amount: Number(data.amount),
        },
      },
    })
  );

  return (
    <Modal
      disableAutoFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={onClickClose}
        body={
          <>
            <ModalTitle>Add new funding</ModalTitle>
            <Box paddingTop={4}>
              <form onSubmit={handleSubmit} disabled={isSubmitting}>
                <Stack spacing={3}>
                  <ProcessedAtInput
                    controllerProps={{ control }}
                    label="Processed at"
                    inputFormat="yyyy-MM-DD"
                    disableFuture
                  />
                  <CurrencyCodeInput
                    controllerProps={{ control }}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled
                    helperText={
                      errors.currencyCode?.message || `Locked to the Giving Account's currency`
                    }
                    label="Funding currency"
                    select
                  >
                    {Object.values(currencies).map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </CurrencyCodeInput>
                  <AmountInput
                    controllerProps={{ control }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {
                            (currencies as { [key: string]: Currency })[formValues.currencyCode]
                              ?.symbol
                          }
                        </InputAdornment>
                      ),
                      inputProps: {
                        step: 0.01,
                      },
                    }}
                    helperText={errors.amount?.message}
                    label="Amount funded"
                    type="number"
                  />
                  <IdentifierInput
                    controllerProps={{ control }}
                    helperText={errors.identifier?.message}
                    label="Transaction identifier"
                    type="text"
                  />
                  <NoteInput
                    controllerProps={{ control }}
                    helperText={
                      errors.note?.message ||
                      'Internal notes for the Deed Finance team. Not visible to customers'
                    }
                    multiline
                    minRows={3}
                    label="Notes"
                    type="text"
                  />
                  <ReceiptUrlInput
                    controllerProps={{ control }}
                    helperText={
                      errors.receiptUrl?.message ||
                      `The screenshot/print out of the confirmation from ${givingAccount.provider}`
                    }
                    linkPreview
                    s3Acl="private"
                    s3Bucket="secure"
                    label="Transaction receipt/documentation"
                  />
                  {addGivingAccountFundingTransactionMutationError && (
                    <Alert severity="error">
                      Failed to add funding:
                      <br />
                      <br />
                      {addGivingAccountFundingTransactionMutationError?.message}
                    </Alert>
                  )}
                </Stack>
              </form>
            </Box>
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={isSubmitting}
              onClick={(): void => {
                onClickClose();
              }}
              endIcon={<CancelOutlined />}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={!isValid || isSubmitting}
              onClick={async (): Promise<void> => {
                try {
                  await handleSubmit();
                  onClickClose();
                } catch (error) {
                  // @NOTE-RT: Just swallow this error silently
                }
              }}
              endIcon={<AddCircleOutline />}
            >
              Add funding
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};

export default AddGivingAccountFundingModal;
